import { NULL_UUID_ID } from '@/constants'
import type { CategoryEntityModel, CategoryInterface } from '@/types'

export type BoundCharacteristicCategory = {
  id: string
  code: number
  description: string
  status: 'DRAFT' | 'APPROVED' | 'REJECTED'
  parent?: CategoryEntityModel | null
}

export type CategorySelectionNode = {
  id: string
  code: number
  description: string
  status?: 'DRAFT' | 'APPROVED' | 'REJECTED'
  parentId: string
  parentDescription: string
  path: string
  depth: number
  ancestorIds: string[]
  descendantIds: string[]
  children: CategorySelectionNode[]
}

type PathNode = {
  id: string
  description: string
}

export function buildCategorySelectionTree(
  categoriesTree: CategoryInterface[]
): CategorySelectionNode[] {
  const walk = (
    nodes: CategoryInterface[],
    path: PathNode[]
  ): CategorySelectionNode[] => {
    const sortedNodes = [...nodes].sort((a, b) =>
      a.category.description.localeCompare(b.category.description, 'ru')
    )

    return sortedNodes.map((node) => {
      const nextPath = [
        ...path,
        {
          id: node.category.id,
          description: node.category.description,
        },
      ]
      const children = walk(node.childCategories, nextPath)
      const descendantIds = children.flatMap((childNode) => [
        childNode.id,
        ...childNode.descendantIds,
      ])

      return {
        id: node.category.id,
        code: node.category.code,
        description: node.category.description,
        status: node.category.status,
        parentId:
          node.category.parentId === NULL_UUID_ID ? '' : node.category.parentId,
        parentDescription: path[path.length - 1]?.description ?? '',
        path: nextPath.map((pathNode) => pathNode.description).join(' / '),
        depth: path.length,
        ancestorIds: path.map((pathNode) => pathNode.id),
        descendantIds,
        children,
      }
    })
  }

  return walk(categoriesTree, [])
}

export function buildCategorySelectionMap(nodes: CategorySelectionNode[]) {
  const nodesById = new Map<string, CategorySelectionNode>()

  const walk = (treeNodes: CategorySelectionNode[]) => {
    treeNodes.forEach((node) => {
      nodesById.set(node.id, node)
      walk(node.children)
    })
  }

  walk(nodes)

  return nodesById
}

export function getConflictingCategoryIds(
  selectedIds: Iterable<string>,
  nodesById: Map<string, CategorySelectionNode>
) {
  const conflictingIds = new Set<string>()

  for (const selectedId of selectedIds) {
    const node = nodesById.get(selectedId)
    if (!node) continue

    node.ancestorIds.forEach((ancestorId) => conflictingIds.add(ancestorId))
    node.descendantIds.forEach((descendantId) =>
      conflictingIds.add(descendantId)
    )
  }

  return conflictingIds
}

export function flattenVisibleCategoryTree(
  nodes: CategorySelectionNode[],
  expandedIds: Set<string>
) {
  const visibleNodes: CategorySelectionNode[] = []

  const walk = (treeNodes: CategorySelectionNode[]) => {
    treeNodes.forEach((node) => {
      visibleNodes.push(node)

      if (node.children.length > 0 && expandedIds.has(node.id)) {
        walk(node.children)
      }
    })
  }

  walk(nodes)

  return visibleNodes
}

export function filterCategorySelectionTree(
  nodes: CategorySelectionNode[],
  query: string
): CategorySelectionNode[] {
  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery) return nodes

  const walk = (treeNodes: CategorySelectionNode[]): CategorySelectionNode[] =>
    treeNodes.reduce<CategorySelectionNode[]>((accumulator, node) => {
      const nextChildren = walk(node.children)
      const isMatched =
        node.description.toLowerCase().includes(normalizedQuery) ||
        String(node.code).includes(normalizedQuery) ||
        node.parentDescription.toLowerCase().includes(normalizedQuery) ||
        node.path.toLowerCase().includes(normalizedQuery)

      if (!isMatched && nextChildren.length === 0) return accumulator

      accumulator.push({
        ...node,
        children: nextChildren,
      })

      return accumulator
    }, [])

  return walk(nodes)
}

export function collectExpandableIds(nodes: CategorySelectionNode[]) {
  const expandableIds = new Set<string>()

  const walk = (treeNodes: CategorySelectionNode[]) => {
    treeNodes.forEach((node) => {
      if (node.children.length > 0) {
        expandableIds.add(node.id)
        walk(node.children)
      }
    })
  }

  walk(nodes)

  return expandableIds
}
